import React, { useEffect, useMemo, useState } from 'react';
import { MobileIcon, LaptopIcon, EyeIcon, CircleExclamationIcon } from '@revfluence/fresh-icons/solid/esm';
import { message } from '@revfluence/fresh';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Card, CardHeader } from '@frontend/shadcn/components/ui/card';
import { Switcher } from '@frontend/app/refresh-components';
import { useParams } from 'react-router-dom';
import {
  useGetContentGuidelineTemplatesForProject,
  useGetTermsSettingsForProgram,
  useProjectByIdQuery,
  useUploadContent,
} from '@frontend/app/hooks';
import { useAuth } from '@frontend/context/authContext';
import { useGetClientLogoQuery } from '@frontend/app/hooks/useGetClientLogoQuery';
import { useSaveProgramMutation } from '@frontend/app/containers/Communities/AddOrEditCommunity/hooks/useSaveProgramMutation';
import { logger } from '@common';
import { useUpdateProgramOnboardingTemplate } from './hooks/useUpdateProgramOnboardingTemplate';
import {
  transformAmplifyTemplateToPayload,
  transformPulseTemplateToPayload,
  useFullPreviewUrl,
  useRecruitment,
} from './RecruitmentContext';
import { PreviewContainer } from './Components/PreviewContainer';
import { ViewMode } from './constants';
import { Hero } from './Components/Amplify/Hero';
import { About } from './Components/Amplify/About';
import { Perks } from './Components/Amplify/Perks';
import { Persona } from './Components/Amplify/Persona';
import { Pages } from './Components/Pages';
import { BriefPagePreview } from './Components/BriefPagePreview';
import { Forms } from './Components/Amplify/Forms';
import { ProductPagePreview } from './Components/ProductPagePreview';
import { Hero as HeroPulse } from './Components/Pulse/Hero';
import { Form } from './Components/Form';
import { Confirmation } from './Components/Confirmation';

// Helper function to convert base64 string to File object
function base64StringToFile(base64String: string, fileName: string): File {
  const parts = base64String.split(',');
  if (parts.length !== 2) {
    // Or handle this more gracefully depending on expected inputs
    throw new Error('Invalid base64 string format. Expected "data:[<mediatype>];base64,<data>".');
  }
  const contentTypeMatch = parts[0].match(/:(.*?);/);
  const contentType = contentTypeMatch && contentTypeMatch[1] ? contentTypeMatch[1] : 'image/png'; // Default to png if somehow not found
  const extension = contentType.split('/')[1] || 'png';

  const fileNameWithExtension = `${fileName}.${extension}`;

  try {
    const byteCharacters = atob(parts[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], fileNameWithExtension, { type: contentType });
  } catch (e) {
    // Catching atob errors for invalid base64 content
    console.error('Failed to decode base64 string:', e);
    throw new Error('Failed to decode base64 string.');
  }
}

export const TemplatePreview = () => {
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.MOBILE);

  const { pulseTemplate, amplifyTemplate, settings, selectedPage, hasUnsavedChanges, revertChanges, setHasUnsavedChanges } = useRecruitment();

  const { templateId, projectId } = useParams<{ templateId: string; projectId: string }>();

  const { data: project } = useProjectByIdQuery({
    variables: {
      id: Number(projectId),
    },
    skip: !projectId,
  });

  const { clientInfo } = useAuth();

  const { logoUrl: clientLogo } = useGetClientLogoQuery();

  const {
    error: uploadError,
    upload: uploadImage,
    isUploading,
  } = useUploadContent({
    serviceName: 'community',
    parentFolder: 'temp',
    keepFileName: true,
  });

  const selectedAmplifyPage = amplifyTemplate?.pages?.[selectedPage];

  const selectedPulsePage = pulseTemplate?.pages?.[selectedPage];

  const briefPage = selectedAmplifyPage?.brief || selectedPulsePage?.brief;

  const { data: termsSettings, loading: isTermsSettingsLoading } = useGetTermsSettingsForProgram({
    variables: {
      programId: Number(projectId),
    },
    skip: !projectId || !briefPage,
  });

  const { data: guidelinesData, loading: isGuidelinesLoading } = useGetContentGuidelineTemplatesForProject({
    skip: !projectId || !briefPage,
    variables: {
      programId: Number(projectId),
    },
    fetchPolicy: 'no-cache',
  });

  const contentGuideline = useMemo(() => {
    if (!guidelinesData) {
      return null;
    }
    const { contentGuidelines, defaultContentGuidelines, memberContentGuidelines, termsContentGuidelines } =
      guidelinesData.guidelines;
    const allContentGuidelines = [
      ...(contentGuidelines ?? []),
      ...(defaultContentGuidelines ?? []),
      ...(memberContentGuidelines ?? []),
      ...(termsContentGuidelines ?? []),
    ];
    return allContentGuidelines.find((guideline) => guideline.id === termsSettings?.termsTemplate?.contentGuidelineId);
  }, [guidelinesData, termsSettings?.termsTemplate?.contentGuidelineId]);

  // setViewMode based on the screen size and update the state every time the screen size changes
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setViewMode(ViewMode.DESKTOP);
      } else {
        setViewMode(ViewMode.MOBILE);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    if (uploadError && !uploadError.isUploadAborted) {
      message.error(uploadError.message);
    }
  }, [uploadError]);

  const [saveProgram] = useSaveProgramMutation(project?.project?.communityId);

  const [updateProgramOnboardingTemplate, { loading: isUpdating }] = useUpdateProgramOnboardingTemplate({
    onCompleted: (onboardingTemplate) => {
      saveProgram({
        variables: {
          program: {
            id: Number(projectId),
            published: true,
            customLandingPagePath: onboardingTemplate?.template?.pageUrl || project?.project?.customLandingPagePath,
          }
        },
        onError: (error) => {
          logger.error('TemplatePreview.onCompleted', error);
          message.error(error.message || 'Failed to save template');
        },
      });
      message.success('Template saved successfully');
      setHasUnsavedChanges(false);
    },
    onError: (error) => {
      message.error(error.message || 'Failed to save template');
    },
  });

  const fullUrl = useFullPreviewUrl();

  const handleSave = async () => {
    let logoImage: string = null;

    if (settings.logo) {
      if (settings.logo.startsWith('data:image')) {
        try {
          const imageFile = base64StringToFile(settings.logo, 'campaign_logo');
          logoImage = await uploadImage(imageFile, 'image');
        } catch (e) {
          message.error(`Failed to upload logo: ${e.message || 'Unknown error'}`);
          return;
        }
      } else {
        logoImage = settings.logo;
      }
    }

    if (pulseTemplate) {
      let heroCarouselImages: string[] = [];
      if (pulseTemplate.pages[0].hero.carouselImages) {
        heroCarouselImages = await Promise.all(
          pulseTemplate.pages[0].hero.carouselImages.map(async (image) => {
            if (image.startsWith('data:image')) {
              const imageFile = base64StringToFile(image, 'hero_carousel_image');
              return uploadImage(imageFile, 'image');
            }
            return image;
          }),
        );
      }
      const payload = transformPulseTemplateToPayload(pulseTemplate, settings, logoImage, heroCarouselImages);
      updateProgramOnboardingTemplate({
        variables: {
          input: {
            data: payload,
            id: Number(templateId),
          },
        },
      });
    } else if (amplifyTemplate) {
      let heroCarouselImages: string[] = [];
      if (amplifyTemplate.pages[0].home.hero.carouselImages) {
        heroCarouselImages = await Promise.all(
          amplifyTemplate.pages[0].home.hero.carouselImages.map(async (media) => {
            if (media.startsWith('data:image') || media.startsWith('data:video')) {
              const file = base64StringToFile(
                media,
                media.startsWith('data:image') ? 'hero_carousel_image' : 'hero_carousel_video',
              );
              const type = media.startsWith('data:image') ? 'image' : 'video';
              return uploadImage(file, type);
            }
            return media;
          }),
        );
      }
      let aboutContentImages: string[] = [];
      if (amplifyTemplate.pages[0].home.about.carouselImages) {
        aboutContentImages = await Promise.all(
          amplifyTemplate.pages[0].home.about.carouselImages.map(async (image) => {
            if (image.startsWith('data:image')) {
              const imageFile = base64StringToFile(image, 'about_content_image');
              return uploadImage(imageFile, 'image');
            }
            return image;
          }),
        );
      }
      let personaImage: string = null;
      if (amplifyTemplate.pages[0].home.persona.personaImage) {
        if (amplifyTemplate.pages[0].home.persona.personaImage.startsWith('data:image')) {
          const imageFile = base64StringToFile(amplifyTemplate.pages[0].home.persona.personaImage, 'persona_image');
          personaImage = await uploadImage(imageFile, 'image');
        } else {
          personaImage = amplifyTemplate.pages[0].home.persona.personaImage;
        }
      }
      const payload = transformAmplifyTemplateToPayload(
        amplifyTemplate,
        settings,
        logoImage,
        heroCarouselImages,
        aboutContentImages,
        personaImage,
      );
      updateProgramOnboardingTemplate({
        variables: {
          input: {
            data: payload,
            id: Number(templateId),
          },
        },
      });
    }
  };
  const isHeroAvailable = amplifyTemplate?.pages?.[selectedPage]?.home?.hero?.showSection;
  const isAboutAvailable = amplifyTemplate?.pages?.[selectedPage]?.home?.about?.showSection;
  const isPerksAvailable = amplifyTemplate?.pages?.[selectedPage]?.home?.perks?.showSection;
  const isPersonaAvailable = amplifyTemplate?.pages?.[selectedPage]?.home?.persona?.showSection;
  const isFormAvailable = amplifyTemplate?.pages?.[selectedPage]?.home?.form?.showSection;

  const isHeroPulseAvailable = pulseTemplate?.pages?.[selectedPage]?.hero;

  const isLoading = isUploading || isUpdating;

  return (
    <Card className="rounded-2xl w-full">
      <CardHeader className="p-4 border-b border-border">
        <div className="flex justify-between">
          <div className="flex items-center gap-2">
            <Switcher
              items={[
                { key: ViewMode.MOBILE, icon: <MobileIcon fontSize={12} /> },
                { key: ViewMode.DESKTOP, icon: <LaptopIcon fontSize={12} /> },
              ]}
              type="single"
              activeKeys={[viewMode]}
              onChange={(value) => setViewMode(value[0] as ViewMode)}
            />
            <Button variant="ghost" size="headerIcon" onClick={() => window.open(fullUrl, '_blank')}>
              <EyeIcon />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <div className="rounded flex items-center gap-1 text-warning">
                <CircleExclamationIcon className="w-4 h-4" />
                <span className="text-sm font-inter leading-normal">Unsaved Changes</span>
              </div>
            )}
            <Button variant="outline" size="sm" disabled={isLoading} onClick={revertChanges}>
              Cancel
            </Button>
            <Button variant="default" size="sm" loading={isLoading} onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <div className="w-full px-6 pt-2 pb-4 bg-grey-2 flex flex-col justify-center items-center gap-4 h-[calc(100vh-80px-38px-24px-65px-28px)] rounded-b-2xl">
        <PreviewContainer view={viewMode}>
          {selectedAmplifyPage?.home && (
            <>
              {isHeroAvailable && (
                <Hero
                  view={viewMode}
                  logo={settings.logo}
                  title={selectedAmplifyPage.home.hero.title || 'Hero Title'}
                  description={selectedAmplifyPage.home.hero.description || 'Hero Description'}
                  joinButtonText={selectedAmplifyPage.home.hero.primaryButtonLabel || 'Add Label'}
                  campaignStartDate={settings.campaignDates.startDate}
                  campaignEndDate={settings.campaignDates.endDate}
                  images={
                    selectedAmplifyPage.home.hero.carouselImages?.map((image) => ({
                      url: image,
                      alt: 'Hero Image',
                    })) || []
                  }
                  backgroundColor={settings.bgPrimary}
                  bodyTextColor={settings.bodyPrimary}
                  bodyTextFontSize={Number(settings.bodyFont.fontSize)}
                  bodyTextFontFamily={settings.bodyFont.fontFamily}
                  titleColor={settings.titlePrimary}
                  titleFontSize={Number(settings.titleFont.fontSize)}
                  titleFontFamily={settings.titleFont.fontFamily}
                  joinButtonColor={settings.buttonBg}
                  joinButtonTextColor={settings.buttonTextColor}
                  joinButtonFontSize={settings.buttonFont.fontSize}
                  joinButtonFontFamily={settings.buttonFont.fontFamily}
                />
              )}
              {isAboutAvailable && (
                <About
                  view={viewMode}
                  title={selectedAmplifyPage.home.about.title || 'About Title'}
                  description={selectedAmplifyPage.home.about.description || 'About Description'}
                  backgroundColor={settings.bgSecondary}
                  titleColor={settings.titleSecondary}
                  titleFontSize={Number(settings.titleFont.fontSize)}
                  titleFontFamily={settings.titleFont.fontFamily}
                  bodyTextColor={settings.bodySecondary}
                  bodyTextFontSize={Number(settings.bodyFont.fontSize)}
                  bodyTextFontFamily={settings.bodyFont.fontFamily}
                  images={
                    selectedAmplifyPage.home.about.carouselImages?.map((image) => ({
                      url: image,
                      alt: 'About Image',
                    })) || []
                  }
                />
              )}
              {isPerksAvailable && (
                <Perks
                  view={viewMode}
                  title={selectedAmplifyPage.home.perks.title || 'Program Perks'}
                  perks={
                    selectedAmplifyPage.home.perks.listItems?.map((item) => ({
                      text: item.text,
                      icon: item.icon,
                    })) || []
                  }
                  listStyleType={selectedAmplifyPage.home.perks.listStyleType || 'number'}
                  backgroundColor={settings.bgPrimary}
                  titleColor={settings.titlePrimary}
                  titleFontSize={Number(settings.titleFont.fontSize)}
                  titleFontFamily={settings.titleFont.fontFamily}
                  bodyTextColor={settings.bodyPrimary}
                  bodyTextFontSize={Number(settings.bodyFont.fontSize)}
                  bodyTextFontFamily={settings.bodyFont.fontFamily}
                />
              )}
              {isPersonaAvailable && (
                <Persona
                  view={viewMode}
                  image={selectedAmplifyPage.home.persona.personaImage || ''}
                  listStyleType={selectedAmplifyPage.home.persona.listStyleType || 'bullet'}
                  title={selectedAmplifyPage.home.persona.title || 'Persona Title'}
                  items={
                    selectedAmplifyPage.home.persona.listItems?.map((item) => ({
                      text: item.text,
                      icon: item.icon,
                    })) || []
                  }
                  backgroundColor={settings.bgSecondary}
                  titleColor={settings.titleSecondary}
                  titleFontSize={Number(settings.titleFont.fontSize)}
                  titleFontFamily={settings.titleFont.fontFamily}
                  bodyTextColor={settings.bodySecondary}
                  bodyTextFontSize={Number(settings.bodyFont.fontSize)}
                  bodyTextFontFamily={settings.bodyFont.fontFamily}
                />
              )}
              {isFormAvailable && (
                <Forms
                  view={viewMode}
                  title={selectedAmplifyPage.home.form.title || 'Form Title'}
                  description={selectedAmplifyPage.home.form.description || 'Form Description'}
                  backgroundColor={settings.bgPrimary}
                  bodyTextColor={settings.bodyPrimary}
                  cardColor={settings.bgSecondary}
                  buttonColor={settings.buttonBg}
                  buttonTextColor={settings.buttonTextColor}
                  buttonLabel={selectedAmplifyPage.home.form.ctaButtonText || 'Add Label'}
                  buttonFontSize={settings.buttonFont.fontSize}
                  buttonFontFamily={settings.buttonFont.fontFamily}
                  fields={selectedAmplifyPage.home.form.fields || []}
                  socialMedia={Object.entries(settings?.socialAccounts || {})
                    .map(([platform, url]) => ({
                      platform,
                      url,
                    }))
                    .filter((social) => social.url)}
                  footerLinks={[
                    { label: 'Terms of Service', href: 'https://www.aspire.io/legal/terms-of-service' },
                    { label: 'Privacy Policy', href: 'https://www.aspire.io/legal/privacy-policy' },
                  ]}
                />
              )}
            </>
          )}
          {isHeroPulseAvailable && (
            <HeroPulse
              view={viewMode}
              logo={settings.logo}
              title={selectedPulsePage.hero.title}
              description={selectedPulsePage.hero.description}
              joinButtonText={selectedPulsePage.hero.ctaButtonText || 'Add Label'}
              campaignStartDate={settings.campaignDates.startDate}
              campaignEndDate={settings.campaignDates.endDate}
              images={
                selectedPulsePage.hero.carouselImages?.map((image) => ({
                  url: image,
                  alt: 'Hero Image',
                })) || []
              }
              socialMedia={Object.entries(settings?.socialAccounts || {})
                .map(([platform, url]) => ({
                  platform,
                  url,
                }))
                .filter((social) => social.url)}
              listItems={pulseTemplate.pages[0].hero.listItems?.map((item) => ({
                text: item.text,
                icon: item.icon,
              }))}
              listStyleType={pulseTemplate.pages[0].hero.listStyleType}
              backgroundColor={settings.bgPrimary}
              bodyTextColor={settings.bodyPrimary}
              backgroundColorSecondary={settings.bgSecondary}
              bodyTextColorSecondary={settings.bodySecondary}
              bodyTextFontSize={Number(settings.bodyFont.fontSize)}
              bodyTextFontFamily={settings.bodyFont.fontFamily}
              titleColor={settings.titlePrimary}
              titleFontSize={Number(settings.titleFont.fontSize)}
              titleFontFamily={settings.titleFont.fontFamily}
              joinButtonColor={settings.buttonBg}
              joinButtonTextColor={settings.buttonTextColor}
              joinButtonFontSize={settings.buttonFont.fontSize}
              joinButtonFontFamily={settings.buttonFont.fontFamily}
              onSubmit={() => {
                // No-op for preview
              }}
              footerLinks={[
                { label: 'Terms of Service', href: 'https://www.aspire.io/legal/terms-of-service' },
                { label: 'Privacy Policy', href: 'https://www.aspire.io/legal/privacy-policy' },
              ]}
            />
          )}
          {selectedAmplifyPage?.brief && (
            <BriefPagePreview
              brandLogo={clientLogo}
              programName={project?.project?.title}
              brandName={clientInfo?.name}
              title={selectedAmplifyPage.brief.title}
              description={selectedAmplifyPage.brief.description}
              continueButtonText={selectedAmplifyPage.brief.primaryButtonLabel}
              briefTemplate={termsSettings?.termsTemplate}
              contentGuideline={contentGuideline}
              onSubmit={() => {
                // No-op for preview
              }}
              isSubmitting={false}
              backgroundColor={settings.bgPrimary}
              isLoading={isTermsSettingsLoading || isGuidelinesLoading}
            />
          )}
          {selectedAmplifyPage?.product && (
            <ProductPagePreview
              catalogPreviewIframeUrl={selectedAmplifyPage.product.iframeUrl}
              isCatalogPreviewIframeUrlLoading={!selectedAmplifyPage.product.iframeUrl}
              isPreview
              backgroundColor={settings.bgPrimary}
            />
          )}

          {selectedPulsePage?.brief && (
            <BriefPagePreview
              brandLogo={clientLogo}
              programName={project?.project?.title}
              brandName={clientInfo?.name}
              title={selectedPulsePage.brief.title}
              description={selectedPulsePage.brief.description}
              continueButtonText={selectedPulsePage.brief.primaryButtonLabel}
              briefTemplate={termsSettings?.termsTemplate}
              contentGuideline={contentGuideline}
              onSubmit={() => {
                // No-op for preview
              }}
              isSubmitting={false}
              isLoading={isTermsSettingsLoading || isGuidelinesLoading}
            />
          )}

          {selectedPulsePage?.product && (
            <ProductPagePreview
              catalogPreviewIframeUrl={selectedPulsePage.product.iframeUrl}
              isCatalogPreviewIframeUrlLoading={!selectedPulsePage.product.iframeUrl}
              isPreview
              backgroundColor={settings.bgPrimary}
            />
          )}

          {selectedAmplifyPage?.form && (
            <Form
              view={viewMode}
              logo={settings.logo}
              programName={project?.project?.title}
              brandName={clientInfo?.name}
              title={selectedAmplifyPage.form.title}
              description={selectedAmplifyPage.form.description}
              fields={selectedAmplifyPage.form.fields}
              supportEmail={selectedAmplifyPage.form.supportEmail}
              primaryButtonLabel={selectedAmplifyPage.form.primaryButtonLabel}
              backgroundColor={settings.bgPrimary}
            />
          )}
          {selectedPulsePage?.form && (
            <Form
              view={viewMode}
              logo={settings.logo}
              programName={project?.project?.title}
              brandName={clientInfo?.name}
              title={selectedPulsePage.form.title}
              description={selectedPulsePage.form.description}
              fields={selectedPulsePage.form.fields}
              supportEmail={selectedPulsePage.form.supportEmail}
              primaryButtonLabel={selectedPulsePage.form.primaryButtonLabel}
              backgroundColor={settings.bgPrimary}
            />
          )}
          {selectedPulsePage?.confirmation && (
            <Confirmation
              view={viewMode}
              backgroundColor={settings.bgPrimary}
              logo={settings.logo}
              programName={project?.project?.title}
              brandName={clientInfo?.name}
              title={selectedPulsePage.confirmation.primaryMessage}
              description={selectedPulsePage.confirmation.description}
            />
          )}
          {selectedAmplifyPage?.confirmation && (
            <Confirmation
              view={viewMode}
              backgroundColor={settings.bgPrimary}
              logo={settings.logo}
              programName={project?.project?.title}
              brandName={clientInfo?.name}
              title={selectedAmplifyPage.confirmation.primaryMessage}
              description={selectedAmplifyPage.confirmation.description}
            />
          )}
        </PreviewContainer>
        <Pages />
      </div>
    </Card>
  );
};
